import { useRef } from "react";

import { CX, CY, type Frame, type Layer, type LayerProps,polar, TAU, trailing, usePainter } from "./core";

const R = 152;
/** Length of the comet tail behind the hand, radians (~1/11 of the dial). */
const TAIL = 0.55;
/** Idle dots sit back at this share of their usual brightness. */
const RESTING = 0.3;

/** Every third tick is longer; each set is one path so it is one stroke width. */
const ticksPath = (long: boolean) =>
  Array.from({ length: 12 }, (_, i) => i)
    .filter((i) => (i % 3 === 0) === long)
    .map((i) => {
      const a = (i / 12) * TAU - Math.PI / 2;
      const from = polar(a, long ? 176 : 184);
      const to = polar(a, 196);
      return `M${from.x.toFixed(1)},${from.y.toFixed(1)}L${to.x.toFixed(1)},${to.y.toFixed(1)}`;
    })
    .join("");

/** 15 min: a clock ring, its radar hand lighting each dot it sweeps past. */
export const Clock: Layer = Object.assign(
  ({ register }: LayerProps) => {
    const ticks = useRef<SVGGElement>(null);
    const hand = useRef<SVGLineElement>(null);

    usePainter(register, () => (_pts, a, f) => {
      const tip = polar(f.sweep, R);
      hand.current?.setAttribute("x2", tip.x.toFixed(1));
      hand.current?.setAttribute("y2", tip.y.toFixed(1));
      hand.current?.setAttribute("opacity", (0.8 * a).toFixed(2));
      ticks.current?.setAttribute("opacity", (0.6 * a).toFixed(2));
    });

    return (
      <>
        <g ref={ticks} opacity={0} stroke="currentColor" strokeLinecap="round">
          <path d={ticksPath(true)} strokeWidth={2} />
          <path d={ticksPath(false)} strokeWidth={1} />
        </g>
        <line ref={hand} x1={CX} y1={CY} stroke="currentColor" strokeWidth={2} strokeLinecap="round" opacity={0} />
      </>
    );
  },
  {
    closed: true,
    shape: (u: number) => polar(u * TAU - Math.PI / 2, R),
    fade: RESTING,
    // flare where the hand is, decaying to nothing behind it
    light: (_u: number, x: number, y: number, f: Frame) => Math.exp(-trailing(Math.atan2(y - CY, x - CX), f.sweep) / TAIL),
  },
);
